import React, { useContext, useEffect, useState } from "react";

import { ZeosSpinner } from "../../components/Shared/ZeosSpinner";
import { ZeosTable } from "../../components/Shared/ZeosTable/ZeosTable";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { createLogger } from "../../services/Logger";
import { IProjectListItem } from "../../services/zeos-api/contracts/Project";
import {
  IProjectTableItem,
  projectsColumns,
  projectsDataMapper,
} from "../../mock-data/ProjectsTableData";

interface IProps {
  selectedProperty: number | null;
}

const CATEGORY_LABELS: Record<string, string> = {
  "power-generation": "Power Generation",
  battery: "Battery",
  "ev-charging": "EV Charging",
  building: "Building",
  carbon: "Carbon",
  other: "Other",
};

const normalizeCategorySlug = (value?: string | null) => {
  if (!value) {
    return "other";
  }
  const normalized = value
    .toLowerCase()
    .replace(/[_\s]/g, "")
    .replace(/-/g, "");

  if (normalized.includes("powergeneration") || normalized.includes("solar")) {
    return "power-generation";
  }
  if (normalized.includes("battery")) {
    return "battery";
  }
  if (normalized.includes("evcharging") || normalized.includes("evse")) {
    return "ev-charging";
  }
  if (normalized.includes("building")) {
    return "building";
  }
  if (normalized.includes("carbon")) {
    return "carbon";
  }
  return "other";
};

export const ProjectsTable = ({
  selectedProperty,
}: IProps) => {
  const api = useContext(ZeosApiContext);
  const log = createLogger("ProjectsTable");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<IProjectListItem[]>([]);

  useEffect(() => {
    const fetchProjects = async () => {
      if (!selectedProperty) {
        setProjects([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await api.Projects.getProjectsByProperty(
          selectedProperty
        );
        if (response?.message === "error") {
          throw new Error(response?.message || "Unable to load projects.");
        }
        setProjects(response?.data ?? []);
      } catch (err: any) {
        log.error("Failed to load projects", err);
        setError(err?.message || "Unable to load projects.");
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [api.Projects, selectedProperty]);

  if (!selectedProperty) {
    return (
      <div className="bg-white shadow rounded p-6 text-sm text-gray-600">
        Select a property to see its projects.
      </div>
    );
  }

  if (loading) {
    return <ZeosSpinner />;
  }

  if (error) {
    return (
      <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
        {error}
      </div>
    );
  }

  const tableItems: IProjectTableItem[] = projects.map((project) => {
    const categorySlug = normalizeCategorySlug(project.category);
    return {
    key: project.projectId,
    category: CATEGORY_LABELS[categorySlug] || "Other",
    title: project.projectName || "",
    description: project.description || "",
    status: project.status || "",
    };
  });

  return (
    <ZeosTable
      unmappedData={tableItems}
      dataMapping={projectsDataMapper}
      columnDefs={projectsColumns}
      searchable={false}
      pagination={true}
      initialOrder={[[1, "asc"]]}
      emptyMessage="No projects found for this property yet."
      showFilters={false}
    />
  );
};
